import Alert from '@mui/material/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';

import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import { TabPanel } from '~components/TabPanel';
import { useNormalisedTabIndex } from '~hooks/useNormalisedTabIndex';
import DiallerGroupLiveDash from '~pages/CampaignManagement/DiallerGroupDetails/DiallerGroupLiveDash';
import useDiallerGroup from '~pages/CampaignManagement/DiallerGroupDetails/useDiallerGroup';
import { CampaignType, DiallerType } from '~pages/CampaignManagement/domain';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import { useSetPageTitleProps } from '~providers/PageTitleProvider';
import Routes from '~providers/RouteProvider/Routes';

import DiallerGroupAgents from './DiallerGroupAgents';
import DiallerGroupCampaigns from './DiallerGroupCampaigns';
import DiallerGroupQueues from './DiallerGroupQueues';
import DiallerGroupSettings from './DiallerGroupSettings';

// Used to manage visible tab selection
enum ViewTabsType {
  Agents = 1,
  Campaigns,
  LiveDash,
  Settings,
  Queues,
}

// Without this the `show` param is absent on first render, making tabIndex NaN and handing
// <Tabs> a value that matches no tab. Matches the pattern used by the other details views.
const defaultViewTab = ViewTabsType.Agents;

const a11yProps = (index: number) => ({
  'id': `tab-${index}`,
  'aria-controls': `tab-${index}`,
});

const DiallerGroupDetails = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    show: ViewTabsType[defaultViewTab],
  });
  const { hasScope } = useAuth();

  const setPageTitleProps = useSetPageTitleProps();
  const { diallerGroupId } = useParams() as { diallerGroupId: string };
  const { loading, data, error: diallerGroupError, update, archive, reload } = useDiallerGroup(+diallerGroupId);
  const [error, setError] = useState(diallerGroupError);
  const isConnectPredictive = data?.diallerType == DiallerType.Connect && data?.campaignType == CampaignType.Predictive;

  const tabs: { name: string; value: ViewTabsType; enabled: boolean }[] = [
    {
      name: 'Agents',
      value: ViewTabsType.Agents,
      enabled: hasScope(AccessScope.CanViewGroupAgents),
    },
    {
      name: 'Campaigns',
      value: ViewTabsType.Campaigns,
      enabled: true,
    },
    {
      name: 'Live Dash',
      value: ViewTabsType.LiveDash,
      enabled: data !== undefined && data.diallerType == DiallerType.SIP,
    },
    {
      name: 'Queues',
      value: ViewTabsType.Queues,
      enabled: data !== undefined && data.diallerType == DiallerType.SIP,
    },
    {
      name: 'Settings',
      value: ViewTabsType.Settings,
      enabled: hasScope(AccessScope.CanViewDiallerGroupSettings),
    },
  ];
  const allowedTabs = tabs.filter((tab) => tab.enabled);
  const tabIndex = useNormalisedTabIndex(ViewTabsType, searchParams, setSearchParams, defaultViewTab);
  const initialLoad = loading && !data;

  useEffect(() => {
    reload();
  }, [tabIndex]);

  useEffect(() => {
    if (data) {
      // Set page title
      setPageTitleProps({ pageName: data.name });

      // fallback to the default one when no permission (can happen when the show parameter in the URL is manually updated)
      if (!allowedTabs.find((i) => i.value == tabIndex)) {
        // the default tab, fallback to this tab when no permission on the specified tab
        const fallbackViewTab = allowedTabs.length > 0 ? allowedTabs[0].value : undefined;
        if (fallbackViewTab) {
          setSearchParams({
            show: ViewTabsType[fallbackViewTab],
          });
        } else {
          setError('You do not have permission to view this information');
        }
      }
    }
  }, [data]);

  const onTabChange = (e: ChangeEvent<{}>, tabIndex: number) => {
    setSearchParams({
      show: ViewTabsType[tabIndex],
    });
  };

  const errorAction = () => {
    navigate(Routes.diallerConfig.path);
  };

  const errorDisplay = error ? (
    <EmptyState
      type='error'
      text={error}
      subText='Click the button below to return to dialler configuration.'
      action={errorAction}
      actionText='Dialler Config'
    />
  ) : null;

  return (
    <AsyncLoader isLoading={initialLoad} error={errorDisplay}>
      <Breadcrumbs style={{ marginBottom: 16 }} aria-label='breadcrumb'>
        <Link underline='hover' color='inherit' component={RouterLink} to={Routes.diallerConfig.path}>
          Dialler Configuration
        </Link>
        <Typography color='textPrimary'>Dialler Group Details</Typography>
      </Breadcrumbs>

      {data === undefined && (
        <EmptyState
          type='not-found'
          text='Sorry!'
          subText='We are unable to find the page you are looking for. Click the button below to return to campaign details.'
          action={errorAction}
          actionText='Campaigns Details'
        />
      )}

      {data !== undefined && (
        <>
          <Typography variant='h4' component='h1' gutterBottom>
            {data.name}
          </Typography>

          {isConnectPredictive && data.needsOutboundNumber > 0 && (
            <Alert sx={{ marginBottom: '11.9px', width: '100%' }} variant='filled' severity='warning'>
              {data.needsOutboundNumber} campaign{data.needsOutboundNumber > 1 ? 's' : ''} associated with this group
              needs an Outbound Connect CLI configured before this group can be used in a predictive manner.
            </Alert>
          )}

          {isConnectPredictive && data.needsOutboundNumber === 0 && data.predictiveSettings?.outboundQueue === null && (
            <Alert sx={{ marginBottom: '11.9px', width: '100%' }} variant='filled' severity='warning'>
              This group needs an outbound queue configured before it can be used in a predictive manner.
            </Alert>
          )}

          <Tabs
            orientation='horizontal'
            variant='scrollable'
            onChange={onTabChange}
            value={tabIndex}
            indicatorColor='primary'
            aria-label='Vertical tabs example'>
            {allowedTabs.map(({ name, value }) => (
              <Tab key={value} label={name} {...a11yProps(value)} value={value} />
            ))}
          </Tabs>
          <Divider variant='fullWidth' component='hr' />

          <TabPanel value={tabIndex} index={ViewTabsType.Agents}>
            <DiallerGroupAgents />
          </TabPanel>

          <TabPanel value={tabIndex} index={ViewTabsType.Campaigns}>
            <DiallerGroupCampaigns diallerGroup={data} reloadGroup={reload} />
          </TabPanel>

          <TabPanel value={tabIndex} index={ViewTabsType.LiveDash}>
            <DiallerGroupLiveDash diallerGroup={data} />
          </TabPanel>

          <TabPanel value={tabIndex} index={ViewTabsType.Queues}>
            <DiallerGroupQueues diallerGroupId={diallerGroupId} />
          </TabPanel>

          <TabPanel value={tabIndex} index={ViewTabsType.Settings}>
            <DiallerGroupSettings diallerGroup={data} update={update} archive={archive} />
          </TabPanel>
        </>
      )}
    </AsyncLoader>
  );
};

export default DiallerGroupDetails;
